import { useMemo } from "react";
import _ from "lodash";
import { PAGE_TYPE } from "../../types/pages.type";
import { itemPercentage } from "../../utilis/common";
import { calculateDiffBetweenPercentages, pickComparisonColor } from "../../utilis/percentage-comparison";

/**
 * Hook to calculate comparison data based on page type (admission vs census-based pages)
 * Extracts common comparison calculation logic used across DayCardList, DxCardList, HospitalList, etc.
 *
 * @param {Object} options Configuration options
 * @param {Array} options.data Primary data array
 * @param {Array} options.dataComparison Comparison data array
 * @param {string} options.page Page type (e.g., PAGE_TYPE.ADMISSION, PAGE_TYPE.HOSPITAL)
 * @param {Object} options.filter Primary filter with date range
 * @param {Object} options.filterComparison Comparison filter with date range
 * @param {number} options.averageCensus Average census value for non-admission pages
 * @param {number} options.averageCensusComparison Comparison average census
 * @param {number} options.admissionCompareAgainst Value to compare against for admission pages
 * @param {number} options.admissionCompareAgainstComparison Comparison value for admission pages
 * @param {boolean} options.reverseColors Whether to reverse comparison colors
 * @param {number} options.projectionDays Number of projection days
 * @param {string} options.valueField Field name for the value (default: 'value')
 * @param {string} options.totalField Field name for total (default: 'total')
 * @param {string} options.idField Field name for ID matching (default: '_id')
 * @param {boolean} options.comparingAgainstDifferentFacility Whether comparing against different facility
 * @returns {Array} Data array with comparison properties added
 */
const usePageTypeComparison = ({
  data = [],
  dataComparison = [],
  page,
  filter,
  filterComparison,
  averageCensus,
  averageCensusComparison,
  admissionCompareAgainst,
  admissionCompareAgainstComparison,
  reverseColors = false,
  projectionDays,
  valueField = 'value',
  totalField = 'total',
  idField = '_id',
  comparingAgainstDifferentFacility = false,
}) => {
  const specialPages = [PAGE_TYPE.ADMISSION];
  const isSpecialPage = specialPages.includes(page);
  const isAdmissionPage = page === PAGE_TYPE.ADMISSION;

  const dataWithComparison = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.map((item) => {
      // Find matching comparison item
      let itemComparison;
      if (comparingAgainstDifferentFacility) {
        itemComparison = dataComparison?.find((x) => x?.label === item?.label);
      } else {
        itemComparison = _.find(dataComparison, { [idField]: item[idField] });
      }

      const itemValue = item[valueField] ?? item[totalField] ?? 0;
      const comparisonValue = itemComparison?.[valueField] ?? itemComparison?.[totalField] ?? 0;

      let comparisonColor;
      let comparingAgainstScaled;
      let numberOfDays;

      if (isAdmissionPage) {
        // Admission page: compare against admission-specific values
        const valuePercentage = itemPercentage(itemValue, admissionCompareAgainst, "number");
        const valueComparisonPercentage = itemPercentage(
          comparisonValue,
          admissionCompareAgainstComparison,
          "number"
        );

        const result = calculateDiffBetweenPercentages(
          valuePercentage,
          valueComparisonPercentage,
          reverseColors,
          filter,
          filterComparison,
          comparisonValue,
          projectionDays
        );

        numberOfDays = result.numberOfDays;
        comparingAgainstScaled = result.itemTotalComparisonScaled;
        comparisonColor = pickComparisonColor(result.percentageDiff, isSpecialPage);
      } else {
        // Non-admission pages: compare against average census
        const percentageOfAverageCensus = itemPercentage(itemValue, averageCensus, "number");
        const percentageOfAverageCensusComparison = itemPercentage(
          comparisonValue,
          averageCensusComparison,
          "number"
        );

        const result = calculateDiffBetweenPercentages(
          percentageOfAverageCensus,
          percentageOfAverageCensusComparison,
          reverseColors,
          filter,
          filterComparison,
          comparisonValue,
          projectionDays
        );

        numberOfDays = result.numberOfDays;
        comparingAgainstScaled = result.itemTotalComparisonScaled;
        comparisonColor = pickComparisonColor(result.percentageDiff, isSpecialPage);
      }

      return {
        ...item,
        comparisonColor,
        comparingAgainst: comparisonValue,
        itemComparison,
        comparingAgainstScaled,
        numberOfDays,
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    dataComparison,
    filter,
    filterComparison,
    averageCensus,
    averageCensusComparison,
    admissionCompareAgainst,
    admissionCompareAgainstComparison,
    reverseColors,
    projectionDays,
    valueField,
    totalField,
    idField,
    comparingAgainstDifferentFacility,
    isAdmissionPage,
    isSpecialPage,
  ]);

  return {
    dataWithComparison,
    isSpecialPage,
    isAdmissionPage,
  };
};

export default usePageTypeComparison;
